import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import useStorages from '@afosto/utils-react/files/useStorages';
import useSubscriptions from '@afosto/utils-react/subscription/useSubscriptions';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useDataSource } from '@afosto/app-components-react/DataSource';
import { useSnackbar } from 'notistack';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import DataList from '../DataList';
import File from './File';
import FileUpload from '../FileUpload';
import PrinterDialog from '../PrinterDialog';
import PrintingFailedDialog from '../PrintingFailedDialog';
import ProgressDialog from '../ProgressDialog';
import StorageUploadDialog from '../StorageUploadDialog';
import printAttachmentsJob from '@afosto/utils-react/spooler/printAttachmentsJob';
import { files as filesApi } from '@afosto/content/api/Files';
import translations from './translations';

const Files = ({
  allowUpload,
  allowFilesystemSync,
  files,
  onAddFile,
  onRemoveFile,
  uploadPath,
}) => {
  const intl = useIntl();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['cnt:files:read']);
  const confirm = useConfirm();
  const [storages] = useStorages();
  const [showPrinterDialog, setShowPrinterDialog] = useState(false);
  const [isPrintingFile, setIsPrintingFile] = useState(false);
  const [showPrintingFileFailed, setShowPrintingFileFailed] = useState(false);
  const [printFile, setPrintFile] = useState(null);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [syncFile, setSyncFile] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const subscriptions = useSubscriptions();
  const hasSpoolerSubscription = subscriptions.some(({ code }) => code === 'spl');

  const dataSource = useDataSource(files, {
    modules: [],
  });

  const handleDeleteFileById = async fileId => {
    try {
      await filesApi.deleteFile(fileId);

      enqueueSnackbar(intl.formatMessage(translations.feedback.fileDeleted), {
        variant: 'success',
      });

      if (onRemoveFile && typeof onRemoveFile === 'function') {
        onRemoveFile(fileId);
      }
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.fileNotDeleted), {
        variant: 'error',
      });
    }
  };

  const handleDeleteFile = async file => {
    try {
      const confirmResponse = await confirm({
        title: intl.formatMessage(translations.confirmDeleteFile.title),
        description: intl.formatMessage(translations.confirmDeleteFile.description),
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage(translations.confirmDeleteFile.buttons.cancel),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.confirmDeleteFile.buttons.delete),
            response: 'delete',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
      });

      if (confirmResponse === 'delete') {
        await handleDeleteFileById(file?.id);
      }
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.fileNotDeleted), {
        variant: 'error',
      });
    }
  };

  const handlePrintFile = async printer => {
    try {
      setIsPrintingFile(true);

      await printAttachmentsJob(
        [{ url: printFile?.url }],
        `Printing attachments for file: ${printFile?.id}`,
        { hubID: printer?.hub_id, printerID: printer?.id },
      );

      enqueueSnackbar(intl.formatMessage(translations.feedback.filePrinted), {
        variant: 'success',
      });

      setPrintFile(null);
    } catch (err) {
      setShowPrintingFileFailed(true);
    } finally {
      setIsPrintingFile(false);
    }
  };

  const handleSelectPrinter = printer => {
    setShowPrinterDialog(false);
    handlePrintFile(printer);
  };

  const handleFileUploadSuccess = () => {
    dataSource.reloadDataSource();
    enqueueSnackbar(intl.formatMessage(translations.feedback.fileUploaded), {
      variant: 'success',
    });
    setShowSyncDialog(false);
  };

  const handleAddFile = () => {
    if (onAddFile && typeof onAddFile === 'function') {
      onAddFile();
    }
  };

  const renderFile = file => {
    const printTypes = ['application/pdf'];
    const allowPrint = hasSpoolerSubscription && printTypes.includes(file?.mime);
    const actions = [
      ...(file?.isPrintable !== false && allowPrint && hasPermissions(['spl:queue:create'])
        ? [
            {
              text: intl.formatMessage(translations.page.filesSection.actions.print.label),
              action: () => {
                setPrintFile(file);
                setShowPrinterDialog(true);
              },
            },
          ]
        : []),
      ...(file?.isDownloadable !== false
        ? [
            {
              text: intl.formatMessage(translations.page.filesSection.actions.download.label),
              action: () => window.open(file.url, '_blank'),
            },
          ]
        : []),
      ...(allowFilesystemSync && storages?.length > 0 && hasPermissions(['cnt:filesystems:write'])
        ? [
            {
              text: intl.formatMessage(
                translations.page.filesSection.actions.uploadToStorage.label,
              ),
              action: () => {
                setSyncFile(file);
                setShowSyncDialog(true);
              },
            },
          ]
        : []),
      ...(file?.isDeletable !== false && hasPermissions(['cnt:files:write'])
        ? [
            {
              text: intl.formatMessage(translations.page.filesSection.actions.delete.label),
              action: () => handleDeleteFile(file),
              color: 'secondary',
            },
          ]
        : []),
    ];

    return <File key={file?.id} actions={actions} file={file} />;
  };

  if (!hasPermissions()) {
    return (
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.page.filesSection.title)}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <CardContent>
          <UnauthorizedComponent showActions={false} />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.page.filesSection.title)}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <DataList
          dataSource={dataSource}
          renderItem={renderFile}
          emptyMessageAlignment="left"
          emptyMessage={intl.formatMessage(translations.page.filesSection.emptyMessage)}
        />
        {allowUpload && hasPermissions(['cnt:files:write']) && (
          <Box px={2} py={1}>
            <FileUpload
              allowMultiple
              onUploadFile={handleAddFile}
              onRemoveFile={fileId => handleDeleteFileById(fileId)}
              onRevertFile={fileId => handleDeleteFileById(fileId)}
              files={[]}
              allowFileSizeValidation
              maxTotalFileSize="8MB"
              uploadPath={`/${uploadPath}`}
            />
          </Box>
        )}
      </Card>

      <PrinterDialog
        isOpen={showPrinterDialog}
        onSelect={handleSelectPrinter}
        onCancel={() => {
          setPrintFile(null);
          setShowPrinterDialog(false);
        }}
      />

      <PrintingFailedDialog
        isOpen={showPrintingFileFailed}
        title={intl.formatMessage(translations.printingFileFailedDialog.title)}
        message={intl.formatMessage(translations.printingFileFailedDialog.message)}
        onRetry={() => {
          setShowPrinterDialog(true);
          setShowPrintingFileFailed(false);
        }}
        onClose={() => {
          setPrintFile(null);
          setShowPrintingFileFailed(false);
        }}
        onDownload={() => {
          window.open(printFile.url, '_blank');
          setPrintFile(null);
          setShowPrintingFileFailed(false);
        }}
        showCloseButton
      />

      <ProgressDialog
        isOpen={isPrintingFile}
        message={intl.formatMessage(translations.printFileProgressDialog.description)}
      />

      <StorageUploadDialog
        file={syncFile}
        isOpen={showSyncDialog}
        onClose={() => {
          setShowSyncDialog(false);
          setSyncFile(false);
        }}
        onError={() => {
          dataSource.reloadDataSource();
        }}
        onSuccess={handleFileUploadSuccess}
        storages={storages}
      />
    </>
  );
};

Files.propTypes = {
  allowFilesystemSync: PropTypes.bool,
  allowUpload: PropTypes.bool,
  files: PropTypes.array,
  onAddFile: PropTypes.func,
  onRemoveFile: PropTypes.func,
  uploadPath: PropTypes.string,
};

Files.defaultProps = {
  allowFilesystemSync: true,
  allowUpload: true,
  files: [],
  onAddFile: undefined,
  onRemoveFile: undefined,
  uploadPath: '',
};

export default Files;
